import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ChargeGeofenceForm.module.scss';
import { UseFieldArrayMethods, UseFormMethods } from 'react-hook-form';
import AddIcon from 'assets/svg/desktop_add_new.svg';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import ChargeGeofenceFormItem from '../chargeGeofenceFormItem/ChargeGeofenceFormItem';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { FaChevronUp, FaChevronDown } from 'react-icons/fa';
import { Col, Row } from 'react-flexbox-grid';
import PercentageFormat from 'common/components/percentageFormat/PercentageFormat';
import { ChargeDto } from 'api/charges/models/ChargeDto';
import { ReactComponent as CostCenterCompleteIcon } from 'assets/svg/icon_centro_custos_completo.svg';
import { ReactComponent as CostCenterIncompleteIcon } from 'assets/svg/icon_centro_custos_incompleto.svg';
import { ReactComponent as CostCenterEmptyIcon } from 'assets/svg/icon_centro_custos_zero.svg';

type Props = {
    form: UseFormMethods<ChargeDto>;
    geofences: UseFieldArrayMethods<any>;
    isDetails: boolean;
    geofencesOptions: SelectValueLabel[];
};

const ChargeGeofenceForm: React.FC<Props> = ({ form, geofences, isDetails, geofencesOptions }: Props) => {
    const { t } = useTranslation();

    const [showRemoveGeofenceModal, setShowRemoveGeofenceModal] = useState<boolean>(false);
    const [geofenseToRemove, setGeofenseToRemove] = useState<number | null>(null);
    const [showgeofences, setShowgeofences] = useState<boolean>(isDetails ? false : true);

    const watchedGeofences = form.watch('geofences') || [];
       const totalPercentage = Math.round(
        watchedGeofences.reduce((sum, item) => {
            const value = typeof item.percentage === 'number' && !isNaN(item.percentage) ? item.percentage : 0;
            return sum + value;
        }, 0) * 100
    ) / 100;

    const addNewGeofence = () => {
        const newIndex = geofences.fields.length;
        geofences.append({});

        const remaining = Math.max(0, 100 - totalPercentage);
        form.setValue(`geofences[${newIndex}]`, {
            geofenceId: null,
            percentage: remaining,
        });
    };

    const onRemoveGeofence = async () => {
        if (geofenseToRemove === null) return;
        geofences.remove(geofenseToRemove);
        setShowRemoveGeofenceModal(false)
    };

    const onCancelRemoveGeofence = () => {
        setGeofenseToRemove(null);
        setShowRemoveGeofenceModal(false);
    };


    return (
        <div className={styles.container}>
            <div className={styles.header} onClick={() => { setShowgeofences(!showgeofences) }}>
                <Row >
                    <Col xs={!isDetails ? 5.5 : 6} style={{ display: 'flex', justifyContent: 'center' }}>
                        <div className={styles.headerText}>
                            <span style={{ display: 'flex', paddingRight: '0.5rem' }}>{showgeofences ? <FaChevronUp /> : <FaChevronDown />}</span>
                            {t('charges.geofences.title')} ({geofences.fields.length})
                        </div>
                    </Col>

                    <Col xs={!isDetails ? 4 : 6} style={{ display: 'flex', justifyContent: 'center' }}>
                        <div className={styles.headerText}>
                            {totalPercentage >= 100 ? <CostCenterCompleteIcon data-tip={t('charges.list.geofence_percentage_complete_tooltip')} /> : <></>}
                            {(totalPercentage < 100 && totalPercentage > 0) ? <CostCenterIncompleteIcon data-tip={t('charges.list.geofence_percentage_incomplete_tooltip')}/> : <></>}
                            {totalPercentage <= 0 ? <CostCenterEmptyIcon data-tip={t('charges.list.geofence_percentage_empty_tooltip')} /> : <></>}
                            &nbsp;
                            {t('charges.geofences.percentage_total')}:&nbsp;<span className={`${totalPercentage > 100 ? styles.errorLabel : ''} `}> <PercentageFormat fixedDecimalScale={true} value={totalPercentage} /></span>
                        </div>
                    </Col>
                    {!isDetails && <Col xs={3}>
                        <div className={styles.headerButton}
                            onClick={e => {
                                e.stopPropagation();
                                addNewGeofence();
                            }}>
                            <span className={styles.headerButtonText}>{t('charges.geofences.add_geofences')}</span>
                            <img src={AddIcon} className={styles.headerButtonIcon} />
                        </div>
                    </Col>}
                </Row>
            </div>

            <div className={showgeofences ? '' : styles.collapsedContent}>
                {geofences.fields.length === 0 && <div className={styles.noInfoText}>
                    <div>{t('charges.geofences.without_geofences')}</div>
                    {!isDetails && <div>{t('charges.geofences.without_geofences_completion')}</div>}
                </div>}

                {geofences.fields.length > 0 && <div className={styles.content}>
                    <div 
                        className={`${styles.containerParentLines}`} 
                    >
                    {geofences.fields.map((field, index) => (
                        <ChargeGeofenceFormItem
                            key={`${field.formId}-${index}`}
                            form={form}
                            geofencesOptions={geofencesOptions}
                            isDetails={isDetails}
                            onRemoveClick={() => {
                                setGeofenseToRemove(index);
                                setShowRemoveGeofenceModal(true);
                            }}
                            index={index}
                        />
                    ))}
                    </div>
                </div>}
            </div>
            <QuestionYesNo
                message={t('common.messages.remove_record')}
                isVisible={showRemoveGeofenceModal}
                onYes={onRemoveGeofence}
                onNo={onCancelRemoveGeofence}
            />
        </div>
    );
}

export default ChargeGeofenceForm;
